import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import qs from 'qs';
import {ImageResizing as imageResize, KalturaHelpers, Utils} from 'poly-js-utils';
import {connectToJSContext, mapMediaSizeToBootstrapGrid, RA_73_STERILIZE_HTML_STRING } from '../utils.jsx';
import LazyImg from '../lazy_img/index.jsx';
import classNames from 'classnames';

const getWidthForCenteredMedia = (sizeMedia) => Math.floor(sizeMedia / 2) * 2;
const getOffsetForCenteredMedia = (sizeMedia) => Math.floor((12 - sizeMedia) / 2);

const Photo = ({imageSrc, sizeMedia, isCentered, mediaAlt}) => {
    const centeredWidth = getWidthForCenteredMedia(sizeMedia);
    const centeredOffset = getOffsetForCenteredMedia(centeredWidth);

    const photoClasses = classNames(
        'col-xs-12',
        {
            [`col-md-${sizeMedia}`]: !isCentered,
            [`col-md-${centeredWidth} col-md-offset-${centeredOffset}`]: isCentered
        }
    );

    if (imageSrc) {
        return (<div className={photoClasses}>
            {mediaAlt
                ? (<LazyImg src={imageSrc} alt={mediaAlt} />)
                : (<LazyImg src={imageSrc} role="presentation" />)
            }
        </div>);
    }
    return null;
};

const Video = ({videoId, sizeMedia, richTextContent, isCentered, mediaAlt}) => {
    const needsDefaultClasses = !sizeMedia && !richTextContent;
    const centeredWidth = getWidthForCenteredMedia(sizeMedia);
    const centeredOffset = getOffsetForCenteredMedia(centeredWidth);

    const videoClasses = classNames(
        'col-xs-12',
        {
            'col-md-8 col-md-offset-2 col-lg-7 col-lg-offset-2-5': needsDefaultClasses && !isCentered,
            [`col-md-${sizeMedia}`]: !needsDefaultClasses && !isCentered,
            [`col-md-offset-${centeredOffset} col-md-${centeredWidth}`]: isCentered
        }
    );

    return (
        <div className={videoClasses}>
            <div id={videoId}>
                <span itemprop="description" content={mediaAlt} />
                <span itemprop="name" content={mediaAlt} />
            </div>
        </div>
    );
};

class RichText extends React.Component {
    handleAnchorsAodaRequirements () {
        const externalWindowLink = this.props.labels && this.props.labels.externalWindowLinkAriaText;
        if (externalWindowLink) {
            setTimeout(() => {
                if (this.richTextElement) {
                    const anchors = this.richTextElement.getElementsByTagName('a');
                    if (anchors && anchors.length > 0) {
                        for (let i = 0; i < anchors.length; i++) {
                            const element = anchors[i];
                            if (element) {
                                const target = element.getAttribute('target');
                                if (target === '_blank') {
                                    const ariaLabel = element.getAttribute('aria-label');
                                    if (!ariaLabel) {
                                        element.setAttribute('aria-label',
                                            `${element.innerText} (${externalWindowLink})`);
                                    }
                                    else if (ariaLabel.indexOf(externalWindowLink) < 0) {
                                        element.setAttribute('aria-label',
                                            `${ariaLabel} (${externalWindowLink})`);
                                    }
                                }
                            }
                        }
                    }
                }
            }, 100);
        }
    }

    componentDidMount() {
        this.handleAnchorsAodaRequirements();
    }

    componentDidUpdate() {
        this.handleAnchorsAodaRequirements();
    }

    render() {
        const {richTextContent, sizeMedia, title, isCentered} = this.props;

        const classes = classNames(
            'col-xs-12',
            {
                [`col-md-${(sizeMedia === 12 ? 12 : 12 - sizeMedia)}`]: !isCentered,
                'mt-20 text-center col-md-offset-2 col-md-8': isCentered
            }
        );

        return (
            <div className={classes}>
                <div className="o-text">
                    {title && <h2 className="o-header">{title}</h2>}
                    <div className="o-copy"
                        ref={element => this.richTextElement = element}
                        dangerouslySetInnerHTML={{
                            __html: RA_73_STERILIZE_HTML_STRING(richTextContent)
                        }}
                    />
                </div>
            </div>
        );
    }
}

const TextOnlyLayout = ({richTextContent, labels, title}) => (
    <div className="hs-background-wrapper">
        <div className="hs-custom-content text-only container">
            <div className="row">
                <RichText richTextContent={richTextContent} labels={labels} sizeMedia="0" title={title} />
            </div>
        </div>
    </div>
);

const MediaLayout =
    ({isContainerFluid, mediaType, positionMedia, richTextContent, sizeMedia, videoId, imageSrc, title, mediaAlt,
        labels}) => (
        <div className="hs-background-wrapper">
            <div
                className=
                    {`hs-custom-content container${(isContainerFluid ? "-fluid" : "")} ${mediaType} ${positionMedia}`}
            >
                <div className="row">

                    { positionMedia === "right" &&
                    <RichText richTextContent={richTextContent} labels={labels} sizeMedia={sizeMedia} title={title} />
                    }

                    {(() => {
                        switch (mediaType) {
                            case "video":
                                return (
                                    <Video
                                        videoId={videoId}
                                        sizeMedia={sizeMedia}
                                        richTextContent={richTextContent}
                                        isCentered={positionMedia === "center"}
                                        mediaAlt={mediaAlt}
                                    />
                                );
                            case "photo":
                                return (
                                    <Photo
                                        imageSrc={imageSrc}
                                        sizeMedia={sizeMedia}
                                        isCentered={positionMedia === "center"}
                                        mediaAlt={mediaAlt}
                                    />
                                );
                            default: return <div />;
                        }
                    })()}

                    { ["left", "center"].includes(positionMedia) &&
                    <RichText
                        richTextContent={richTextContent}
                        sizeMedia={sizeMedia}
                        title={title}
                        labels={labels}
                        isCentered={positionMedia === "center"}
                    />
                    }

                </div>
            </div>
        </div>
    );

const CustomContent = ({customContents, labels, moduleClassName}) => (
    <div className={`hs-custom-content-module ${moduleClassName} hs-section`}>
        {customContents.map((content, i) => {
            content = Object.assign({ key: i }, content);
            switch(content.mediaType) {
                case "none": return <TextOnlyLayout labels={labels} {...content} />;
                case "photo": return <MediaLayout labels={labels} {...content} />;
                case "video": return <MediaLayout labels={labels} {...content} />;
                default: return <div key={i} />;
            }
        })}
    </div>
);

CustomContent.propTypes = {
    customContents: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            richTextContent: PropTypes.string,
            imageSrc: PropTypes.string,
            videoId: PropTypes.string,
            positionMedia: PropTypes.oneOf(['left', 'right', 'center']),
            mediaType: PropTypes.oneOf(['none', 'photo', 'video']),
            mediaAlt: PropTypes.string,
            sizeMedia: PropTypes.int,
            isContainerFluid: PropTypes.bool
        })
    ).isRequired,
    moduleClassName: PropTypes.string.isRequired
};

export {CustomContent};

const mapContentsStateToProps = (content) => {
    const mapping = {
        title: content.title,
        richTextContent: content.rich_text_content,
        imageSrc: imageResize.resize(content, "photo", 800),
        mediaAlt:  (content.media_alt) || "",
        mediaType: (content.show_media && content.show_media[0]) || "none",
        positionMedia: (content.position_media && content.position_media[0]) || "left",
        sizeMedia: (content.size_media && mapMediaSizeToBootstrapGrid(content.size_media[0])) || 0,
        isContainerFluid: Utils.selectBoxIsTrue(content.is_container_fluid)
    };
    if(mapping.mediaType === "video") {
        KalturaHelpers.queueVideoForLoading(content.video_id);
        mapping.videoId = `${content.video_id}-kaltura-video`;
    }
    return mapping;
};

const mapProductsUserStateToProps = state => ({
    customContents: (state.profile.products_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: 'products-user-custom-content'
});
export const ProductsUserCustomContent = connectToJSContext(mapProductsUserStateToProps)(CustomContent);

const mapProductsOrgStateToProps = state => ({
    customContents: (state.theme_data.products_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "products-custom-content"
});
export const ProductsOrgCustomContent = connectToJSContext(mapProductsOrgStateToProps)(CustomContent);

const mapEventsUserStateToProps = state => ({
    customContents: (state.profile.events_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: 'events-user-custom-content'
});
export const EventsUserCustomContent = connectToJSContext(mapEventsUserStateToProps)(CustomContent);

const mapEventsOrgStateToProps = state => ({
    customContents: (state.theme_data.events_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "events-custom-content"
});
export const EventsOrgCustomContent = connectToJSContext(mapEventsOrgStateToProps)(CustomContent);

const mapNewsUserStateToProps = state => ({
    customContents: (state.profile.news_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: 'news-user-custom-content'
});
export const NewsUserCustomContent = connectToJSContext(mapNewsUserStateToProps)(CustomContent);

const mapNewsOrgStateToProps = state => ({
    customContents: (state.theme_data.news_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "news-custom-content"
});
export const NewsOrgCustomContent = connectToJSContext(mapNewsOrgStateToProps)(CustomContent);

const mapCareerUserStateToProps = (state) => ({
    customContents: (state.profile.careers_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "careers-custom-content"
});
export const CareerUserCustomContent = connectToJSContext(mapCareerUserStateToProps)(CustomContent);

const mapCareerOrgStateToProps = (state) => ({
    customContents: (state.theme_data.careers_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "careers-custom-content"
});
const CareerOrgCustomContent = connectToJSContext(mapCareerOrgStateToProps)(CustomContent);

const mapCareerOrgTwoStateToProps = (state) => ({
    customContents: (state.theme_data.careers_page.custom_content_2 || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "careers-custom-content"
});
const CareerOrgCustomContentTwo = connectToJSContext(mapCareerOrgTwoStateToProps)(CustomContent);

const mapCareerOrgThreeStateToProps = (state) => ({
    customContents: (state.theme_data.careers_page.custom_content_3 || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "careers-custom-content"
});
const CareerOrgCustomContentThree = connectToJSContext(mapCareerOrgThreeStateToProps)(CustomContent);

export const careerOrgCustomContentComponents = {
    customContent: CareerOrgCustomContent,
    customContentTwo: CareerOrgCustomContentTwo,
    customContentThree: CareerOrgCustomContentThree
};

const mapHomeUserStateToProps = (state) => ({
    customContents: (state.profile.home_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "home-user-custom-content"
});
export const HomeUserCustomContent = connectToJSContext(mapHomeUserStateToProps)(CustomContent);

const mapHomeOrgStateToProps = (state) => ({
    customContents: (state.theme_data.home_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "home-custom-content home-custom-content-1"
});
const HomeOrgCustomContent = connectToJSContext(mapHomeOrgStateToProps)(CustomContent);

const mapHomeOrgTwoStateToProps = (state) => ({
    customContents: (state.theme_data.home_page.custom_content_2 || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "home-custom-content home-custom-content-2"
});
const HomeOrgCustomContentTwo = connectToJSContext(mapHomeOrgTwoStateToProps)(CustomContent);

const mapHomeOrgThreeStateToProps = (state) => ({
    customContents: (state.theme_data.home_page.custom_content_3 || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "home-custom-content home-custom-content-3"
});
const HomeOrgCustomContentThree = connectToJSContext(mapHomeOrgThreeStateToProps)(CustomContent);

export const homeOrgCustomContentComponents = {
    customContent: HomeOrgCustomContent,
    customContentTwo: HomeOrgCustomContentTwo,
    customContentThree: HomeOrgCustomContentThree
};

const mapAboutMeOrgStateToProps = (state) => ({
    customContents: (state.theme_data.about_page.custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "about-custom-content"
});
export const AboutMeOrgCustomContent = connectToJSContext(mapAboutMeOrgStateToProps)(CustomContent);

const mapAboutMeUserStateToProps = (state) => ({
    customContents: (state.profile.about_me_custom_content || []).map(mapContentsStateToProps),
    labels: state.theme_data.labels,
    moduleClassName: "about-user-custom-content"
});
export const AboutMeUserCustomContent = connectToJSContext(mapAboutMeUserStateToProps)(CustomContent);

const mapErrorStateToProps = (state) => {
    const errorCodes = state.theme_data.errors;
    const fallbackId = 'all';

    const qp = qs.parse(document.location.search.substr(document.location.search.indexOf('?') + 1));
    const status_code = qp.status_code || state.status_code || fallbackId;

    let statusData = _.where(errorCodes, { status_code: status_code + '' });

    // Fall back to our default if nothing exists
    statusData = statusData.length ? statusData : _.where(errorCodes, { status_code: fallbackId });

    return {
        customContents: ((statusData.length && statusData[0].messages) || []).map(mapContentsStateToProps),
        labels: state.theme_data.labels,
        moduleClassName: "error-custom-content"
    };
};
export const ErrorCustomContent = connectToJSContext(mapErrorStateToProps)(CustomContent);



// WEBPACK FOOTER //
// ./components/custom_content/index.jsx